import { useEffect, useRef, useState } from 'react';
import { heicWorkerManager } from '../utils/heicWorkerManager';
import { getCacheKey, getCachedImage, setCachedImage } from '../utils/heicCache';

// Detect a HEIC/HEIF image from its URL (ignoring any query string), e.g. photos
// uploaded from an iPhone. Browsers other than Safari can't decode these, so they
// must be converted to JPEG before being handed to an <img>/antd Image.
const isHeicUrl = (url: string): boolean => {
	if (!url) return false;
	const path = url.toLowerCase().split('?')[0];
	return path.endsWith('.heic') || path.endsWith('.heif');
};

// Deduplicate concurrent conversions of the same URL across all hook instances.
const inFlightConversions = new Map<string, Promise<Blob>>();

const convertHeicUrlToJpeg = async (url: string): Promise<Blob> => {
	const cacheKey = getCacheKey(url);

	const cachedBlob = await getCachedImage(cacheKey);
	if (cachedBlob) {
		return cachedBlob;
	}

	const inFlight = inFlightConversions.get(cacheKey);
	if (inFlight) {
		return inFlight;
	}

	const conversionPromise = (async () => {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const arrayBuffer = await blob.arrayBuffer();
			const jpegBlob = await heicWorkerManager.convert(arrayBuffer);
			setCachedImage(cacheKey, jpegBlob).catch(console.error);
			return jpegBlob;
		} finally {
			inFlightConversions.delete(cacheKey);
		}
	})();

	inFlightConversions.set(cacheKey, conversionPromise);
	return conversionPromise;
};

/**
 * Resolve a full image URL to one a browser can actually render. A `.heic`/`.heif`
 * URL is fetched and converted to JPEG (via the shared HEIC Web Worker + IndexedDB
 * cache, the same path ProtectedImageThumbnails uses) and returned as an object URL;
 * every other URL is returned unchanged. While a HEIC conversion is in flight the
 * hook returns an empty string, and it revokes the object URL on unmount / URL change
 * so blobs aren't leaked. Falls back to the original URL if conversion fails.
 */
export const useHeicSafeSrc = (url: string): string => {
	const [resolvedSrc, setResolvedSrc] = useState<string>(isHeicUrl(url) ? '' : url);
	const objectUrlRef = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		const revokeObjectUrl = () => {
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current);
				objectUrlRef.current = null;
			}
		};

		if (!isHeicUrl(url)) {
			revokeObjectUrl();
			setResolvedSrc(url);
			return;
		}

		setResolvedSrc('');
		convertHeicUrlToJpeg(url)
			.then((jpegBlob) => {
				if (cancelled) return;
				const objectUrl = URL.createObjectURL(jpegBlob);
				objectUrlRef.current = objectUrl;
				setResolvedSrc(objectUrl);
			})
			.catch(() => {
				if (cancelled) return;
				// Conversion failed — fall back to the original URL rather than showing nothing.
				setResolvedSrc(url);
			});

		return () => {
			cancelled = true;
			revokeObjectUrl();
		};
	}, [url]);

	return resolvedSrc;
};
